// PdfCanvasViewer.jsx
// Puur presentationeel: rendert het canvas/world dat usePdfCanvasViewer
// aanstuurt. Geen eigen state, alle interactie/logica zit in de hook.
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { ENABLE_TEXT_LAYER, NATIVE_TOUCH_SCROLL } from "./usePdfCanvasViewer";
import DotLoader, { DOT_LOADER } from "../DotLoader";

// Stijl van de pdf.js-tekstlaag (overgenomen uit pdfjs-dist/web/pdf_viewer.css,
// alleen wat TextLayer nodig heeft). De laag staat in wereld-units (scale 1),
// dus --total-scale-factor is 1; de world-transform doet het zoomen. De tekst
// zelf is transparant, alleen de selectie is zichtbaar.
const TEXT_LAYER_CSS = `
.pdf-text-layer {
  position: absolute; inset: 0; overflow: clip; z-index: 0;
  text-align: initial; line-height: 1; letter-spacing: normal; word-spacing: normal;
  -webkit-text-size-adjust: none; text-size-adjust: none; forced-color-adjust: none;
  transform-origin: 0 0; caret-color: transparent;
  --total-scale-factor: 1;
  --min-font-size: 1;
  --text-scale-factor: calc(var(--total-scale-factor) * var(--min-font-size));
  --min-font-size-inv: calc(1 / var(--min-font-size));
}
.pdf-text-layer span, .pdf-text-layer br {
  color: transparent; position: absolute; white-space: pre; cursor: text;
  transform-origin: 0% 0%; -webkit-user-select: text; user-select: text;
}
.pdf-text-layer > :not(.markedContent), .pdf-text-layer .markedContent span:not(.markedContent) {
  z-index: 1;
  font-size: calc(var(--text-scale-factor) * var(--font-height, 0px));
  transform: rotate(var(--rotate, 0deg)) scaleX(var(--scale-x, 1)) scale(var(--min-font-size-inv));
}
.pdf-text-layer .markedContent { display: contents; }
.pdf-text-layer span[role="img"] { -webkit-user-select: none; user-select: none; cursor: default; }
.pdf-text-layer ::selection { background: rgba(0, 110, 255, 0.28); color: transparent; }
.pdf-text-layer br::selection { background: transparent; }
.pdf-text-layer .endOfContent {
  display: block; position: absolute; inset: 100% 0 0; z-index: 0; cursor: default;
  -webkit-user-select: none; user-select: none;
}
.pdf-text-layer.selecting .endOfContent { top: 0; }
.pdf-panning .pdf-text-layer span { -webkit-user-select: none; user-select: none; cursor: inherit; }
`;

// flip (optioneel, PDF_FLIP in CaseDetailDrawer): draai bij een case-wissel.
// { phase: "out"|"in", seq, ghostTransform, ghostTransition, ghostMs,
//   live: { transform, opacity, transition } }. Bij "out" draait een vaste
// kopie (ghost) van de pagina's zoals ze op dat moment in beeld stonden weg;
// de live viewer (live-stijl) laadt intussen onzichtbaar het volgende pdf.
// Alleen de pagina's draaien, de laad-animatie blijft staan. null = oude gedrag.
const FLIP_PERSPECTIVE = "2400px";


export default function PdfCanvasViewer({ viewer, suppressSpinner = false, flip = null }) {
  const { status, errorMsg, contentReady, pages, viewportRef, worldRef, sizerRef, registerCanvas, registerTextLayer, containerHandlers } = viewer;

  // Ghost: bij de start van "out" (vóór de paint, dus zonder één frame van
  // iets anders) de viewport klonen, de canvassen erin overtekenen en die
  // kopie wegdraaien. Daarna weer leeg — hij staat dan toch al op zijn kant.
  const ghostRef = useRef(null);
  const lastInDegRef = useRef(null);
  // Per flip (seq), niet per fase: de ghost moet blijven draaien als het
  // nieuwe pdf al vóór de naad klaar is en de fase dus al "in" is
  const outKey = flip ? flip.seq : null;
  const flipRef = useRef(flip);
  flipRef.current = flip;
  const contentReadyRef = useRef(contentReady);
  contentReadyRef.current = contentReady;
  useLayoutEffect(() => {
    const host = ghostRef.current;
    const vp = viewportRef.current;
    const f = flipRef.current;
    if (outKey == null || !host || !vp || !f || f.phase !== "out" || !contentReadyRef.current) return undefined;
    const clone = vp.cloneNode(true);
    clone.querySelectorAll(".pdf-text-layer").forEach((el) => el.remove());
    const src = vp.querySelectorAll("canvas");
    const dst = clone.querySelectorAll("canvas");
    src.forEach((c, i) => {
      try { if (c.width && c.height && dst[i]) dst[i].getContext("2d").drawImage(c, 0, 0); } catch (e) { /* leeg laten */ }
    });
    // De klasse van de kloon heeft al de live-stijl van "out" (onzichtbaar,
    // gedraaid): inline terug naar de stand van vóór de klik
    Object.assign(clone.style, { opacity: "1", transform: "none", transition: "none", pointerEvents: "none" });
    clone.removeAttribute("id");
    host.replaceChildren(clone);
    host.style.transition = "none";
    host.style.transform = "none";
    host.style.visibility = "visible";
    const clear = () => { host.replaceChildren(); host.style.visibility = "hidden"; };
    if (f.ghostAnim) {
      // Op de klok van de klik, zodat het nieuwe pdf (inAnim) exact aansluit
      const g = f.ghostAnim;
      // Opnieuw geklikt terwijl de vorige nog indraaide: vanaf die hoek verder
      // i.p.v. eerst terug te springen naar recht
      const lastIn = lastInDegRef.current;
      lastInDegRef.current = null;
      const fromDeg = lastIn && performance.now() - lastIn.at < 100 ? lastIn.deg : 0;
      const anim = host.animate([{ transform: `rotateY(${fromDeg}deg)` }, { transform: g.to }], { duration: g.duration, easing: g.easing, fill: "forwards" });
      anim.startTime = g.startAt;
      const t = setTimeout(clear, Math.max(0, g.startAt + g.duration + 50 - performance.now()));
      return () => { clearTimeout(t); anim.cancel(); clear(); };
    }
    host.getBoundingClientRect(); // startstand vastleggen
    host.style.transition = f.ghostTransition;
    host.style.transform = f.ghostTransform;
    const t = setTimeout(clear, f.ghostMs + 50);
    return () => { clearTimeout(t); clear(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outKey]);
  // Nieuwe pdf indraaien (alleen met inAnim): tot startAt blijft 'ie op zijn
  // kant staan (fill backwards), daarna draait 'ie naar het midden
  const inKey = flip?.inAnim ? flip.seq : null;
  useLayoutEffect(() => {
    const vp = viewportRef.current;
    const a = flipRef.current?.inAnim;
    if (inKey == null || !vp || !a) return undefined;
    const anim = vp.animate([{ transform: a.from }, { transform: "none" }], { duration: a.duration, easing: a.easing, fill: "backwards", endDelay: a.endDelay || 0 });
    anim.startTime = Math.max(a.startAt, document.timeline.currentTime ?? 0);
    return () => {
      // Hoek onthouden voor een ghost die direct hierna start (zie boven).
      // progress is al door de easing heen, dus ook goed in de overshoot.
      const p = anim.effect.getComputedTiming().progress;
      lastInDegRef.current = p == null ? null : { deg: a.fromDeg * (1 - p), at: performance.now() };
      anim.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inKey]);

  // DotLoader alleen gemount tijdens laden (+ de fade-out), zodat zijn
  // tijdlijn (ripple -> boog) bij elke nieuwe PDF opnieuw begint.
  const [loaderMounted, setLoaderMounted] = useState(!contentReady);
  useEffect(() => {
    if (!contentReady) { setLoaderMounted(true); return undefined; }
    const t = setTimeout(() => setLoaderMounted(false), 650);
    return () => clearTimeout(t);
  }, [contentReady]);

  if (status === "error") {
    return (
      <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>{errorMsg || "Kon PDF niet laden"}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%", ...(flip && { perspective: FLIP_PERSPECTIVE }) }}>
      {/* Altijd gemount (i.p.v. conditioneel op status) zodat hij zelf ook
          kan fade-en i.p.v. abrupt te verschijnen/verdwijnen. Tijdens een
          case-wissel (suppressSpinner) laat de drawer zelf al zien dat er
          iets laadt — dan hoeft deze niet ook nog eens te verschijnen. */}
      <Box
        sx={{
          position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          opacity: contentReady || suppressSpinner ? 0 : 1,
          // DotLoader rustiger laten uitdoven (gelijk aan Dracoloader)
          transition: DOT_LOADER ? "opacity 0.6s ease" : "opacity 0.25s ease",
          pointerEvents: "none",
        }}
      >
        {/* Zelfde spinner-stijl als Dracoloader (draco-spinner), voor
            visuele consistentie tussen de Scans- en PDF-tab. */}
        {ENABLE_TEXT_LAYER && <style>{TEXT_LAYER_CSS}</style>}
        {DOT_LOADER ? (loaderMounted && <DotLoader />) : (
          <>
            <style>{`@keyframes pdf-canvas-spin { to { transform: rotate(360deg); } } .pdf-canvas-spinner { width: 28px; height: 28px; border-radius: 50%; border: 1.5px solid rgba(255,255,255,0.12); border-top-color: rgba(255,255,255,0.65); animation: pdf-canvas-spin 0.75s linear infinite; }`}</style>
            <div className="pdf-canvas-spinner" />
            <span style={{ marginTop: 12, color: "rgba(255,255,255,0.4)", fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 400, letterSpacing: "0.02em" }}>Loading...</span>
          </>
        )}
      </Box>
      <Box
        ref={viewportRef}
        {...containerHandlers}
        {...(NATIVE_TOUCH_SCROLL && { "data-pdf-native-scroll": "" })}
        sx={{
          position: "absolute", inset: 0, overflow: "hidden", touchAction: "none", cursor: "default",
          // Touchscherm: echte scroll-container (zie NATIVE_TOUCH_SCROLL)
          ...(NATIVE_TOUCH_SCROLL && {
            overflow: "auto", touchAction: "pan-x pan-y", WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" },
          }),
          // Met flip bepaalt de drawer de zichtbaarheid (zie orderFormFlip)
          opacity: flip ? flip.live.opacity : contentReady ? 1 : 0,
          transition: flip ? flip.live.transition : "opacity 0.3s ease",
          ...(flip && { transform: flip.live.transform, backfaceVisibility: "hidden" }),
          pointerEvents: contentReady ? "auto" : "none",
        }}
      >
        {NATIVE_TOUCH_SCROLL && <div ref={sizerRef} style={{ position: "absolute", top: 0, left: 0, width: 1, height: 1, pointerEvents: "none" }} />}
        <Box ref={worldRef} sx={{ position: "absolute", top: 0, left: 0, transformOrigin: "0 0", willChange: "transform" }}>
          {pages && pages.map((p, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute", left: p.left, top: p.top, width: p.width, height: p.height,
                background: "#fff",
                // Waar de viewport een pagina afsnijdt blijft de rand recht
                borderRadius: "var(--pdf-page-radius, 8px)",
                overflow: "hidden",
                boxShadow: "0 6px 14px -2px rgba(0,0,0,0.5), 0 24px 50px -16px rgba(0,0,0,0.5)",
              }}
            >
              <canvas ref={registerCanvas(i)} style={{ display: "block", width: "100%", height: "100%" }} />
              {/* data-pdf-text-layer: ook gebruikt door de swipe in de drawer
                  (slepen vanaf tekst = selecteren, niet naar het volgende bestand) */}
              {ENABLE_TEXT_LAYER && <div ref={registerTextLayer(i)} className="pdf-text-layer" data-pdf-text-layer="" />}
            </Box>
          ))}
        </Box>
      </Box>
      {/* PDF_FLIP: de wegdraaiende kopie (alleen gevuld tijdens "out") */}
      <div
        ref={ghostRef}
        aria-hidden="true"
        style={{ position: "absolute", inset: 0, pointerEvents: "none", visibility: "hidden", backfaceVisibility: "hidden", transformOrigin: "50% 50%" }}
      />
    </Box>
  );
}
